import {
  notify,
  useAccountNotice,
  useOrg,
  useAccess,
  useResendVerification,
  useServicesSections,
  useSession,
  useSettingsSections,
  useSignOut,
  useStaleData,
  stopPreview,
  useStopImpersonating,
  useSwitchOrg,
} from '@church/data'
import type { Permission } from '@church/permissions'
import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
  useRouter,
} from '@tanstack/react-router'
import { Drawer } from '@base-ui/react/drawer'
import {
  faArrowUpRightFromSquare,
  faBuildingDuotone,
  faCalendarCheckDuotone,
  faCalendarDuotone,
  faBookBibleDuotone,
  faBookBibleLight,
  faChartLineDuotone,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faChildReachingDuotone,
  faChurchDuotone,
  faClipboardCheckDuotone,
  faEnvelopeDuotone,
  faGaugeHighDuotone,
  faGear,
  faGearDuotone,
  faHandHoldingDollarDuotone,
  faIdCard,
  faListUlDuotone,
  faMusicDuotone,
  faPalette,
  faPeopleGroupDuotone,
  faPlus,
  faRightFromBracket,
  faSidebar,
  faSquareCheckDuotone,
  faUsersDuotone,
  faXmark,
  faBuildingLight,
  faCalendarCheckLight,
  faCalendarLight,
  faChartLineLight,
  faChildReachingLight,
  faClipboardCheckLight,
  faEllipsisLight,
  faEnvelopeLight,
  faGaugeHighLight,
  faGearLight,
  faHandHoldingDollarLight,
  faListUlLight,
  faMusicLight,
  faPeopleGroupLight,
  faSquareCheckLight,
  faUsersLight,
  type IconData,
} from '@church/ui-icons'
import { useEffect, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react'
import {
  Button,
  Card,
  Icon,
  IconButton,
  InlineSpinner,
  panelPopup,
  panelViewport,
  PHONE_QUERY,
  SheetHandle,
  sheetPopup,
  sheetViewport,
  useMediaQuery,
} from '../components/ui.tsx'
import { choosePhoneTabs } from '../lib/phone-tabs.ts'
import { usePageCrumbs, type Crumb } from '../lib/breadcrumbs.ts'
import { appName, currentSite, urlOn } from '../lib/site.ts'
import { documentTitle, useDocumentTitle } from '../lib/document-title.ts'
import { designToolsEnabled, openDesignEditor, useDesignTweaks } from '../lib/design-tweaks.ts'
import { useTheme, type ThemeChoice } from '../lib/theme.ts'
import { useTopBarTint } from '../lib/top-bar.ts'
import { ConnectionBanner, RouteNotFound, RouteProgress } from '../components/RouteStates.tsx'
import { PreviewAudio } from '../components/services/AppleMusic.tsx'

/**
 * The authenticated, org-scoped app shell. Requires an active organization —
 * users without one are sent to onboarding.
 *
 * - A top bar at every size: breadcrumbs on the left, the account avatar on
 *   the right (it opens the profile drawer: churches, theme, sign out).
 * - From `sm` up, the menu is a rail of icons on the left that expands to a
 *   labelled sidebar (remembered in this browser).
 * - On a phone, the menu is a bar of labelled icon tabs along the bottom; whatever does
 *   not fit is under More.
 */
export const Route = createFileRoute('/_authed/_shell')({
  beforeLoad: ({ context }) => {
    if (!context.me.activeOrganizationId) throw redirect({ to: '/onboarding' })
  },
  component: ShellLayout,
  // Inside the shell: a missing page keeps the menu.
  notFoundComponent: RouteNotFound,
})

interface NavItem {
  label: string
  icon: IconData
  /** The phone's tab bar draws the light weight: thin lines read better small, in one colour. */
  tabIcon: IconData
  permission: Permission | null
  /**
   * Shown by the access ladders (docs/13-ACCESS.md), not one permission: a
   * ForServices section (`any` is anyone with a place in ForServices), or
   * Settings, for anyone with a section of it to open.
   */
  section?: 'any' | 'plans' | 'songs' | 'people' | 'schedule' | 'sermons' | 'settings'
  to: string
  /** A tab of the services page (ForServices' menu). */
  /** Other addresses that are this entry's too (a song's chart is under Songs). */
  also?: readonly string[]
  /** Lives in another ForHim app: a full link there when hosted (Phase 12g). */
  app?: 'services'
}

/** ForChurch's menu. */
const CHURCH_NAV: NavItem[] = [
  {
    label: 'Dashboard',
    icon: faGaugeHighDuotone,
    tabIcon: faGaugeHighLight,
    permission: null,
    to: '/',
  },
  {
    label: 'People',
    icon: faUsersDuotone,
    tabIcon: faUsersLight,
    permission: 'people:read',
    to: '/people',
  },
  {
    label: 'Lists',
    icon: faListUlDuotone,
    tabIcon: faListUlLight,
    permission: 'people:read',
    to: '/lists',
  },
  {
    label: 'Groups',
    icon: faPeopleGroupDuotone,
    tabIcon: faPeopleGroupLight,
    permission: 'groups:read',
    to: '/groups',
  },
  {
    label: 'Attendance',
    icon: faClipboardCheckDuotone,
    tabIcon: faClipboardCheckLight,
    permission: 'attendance:read',
    to: '/attendance',
  },
  {
    label: 'Events',
    icon: faCalendarDuotone,
    tabIcon: faCalendarLight,
    permission: 'events:read',
    to: '/events',
  },
  {
    label: 'Facilities',
    icon: faBuildingDuotone,
    tabIcon: faBuildingLight,
    permission: 'events:read',
    to: '/facilities',
  },
  {
    label: 'Check-in',
    icon: faChildReachingDuotone,
    tabIcon: faChildReachingLight,
    permission: 'checkin:read',
    to: '/checkin',
  },
  {
    label: 'Giving',
    icon: faHandHoldingDollarDuotone,
    tabIcon: faHandHoldingDollarLight,
    permission: 'giving:read',
    to: '/giving',
  },
  {
    label: 'Services',
    icon: faMusicDuotone,
    tabIcon: faMusicLight,
    permission: null,
    section: 'any',
    to: '/services',
    app: 'services',
  },
  {
    label: 'Messages',
    icon: faEnvelopeDuotone,
    tabIcon: faEnvelopeLight,
    permission: 'messages:read',
    to: '/messages',
  },
  {
    label: 'Reports',
    icon: faChartLineDuotone,
    tabIcon: faChartLineLight,
    permission: 'reports:read',
    to: '/reports',
  },
  {
    label: 'My schedule',
    icon: faCalendarCheckDuotone,
    tabIcon: faCalendarCheckLight,
    permission: null,
    section: 'any',
    to: '/my-schedule',
    app: 'services',
  },
  {
    label: 'My tasks',
    icon: faSquareCheckDuotone,
    tabIcon: faSquareCheckLight,
    permission: null,
    to: '/tasks',
  },
  {
    label: 'Settings',
    icon: faGearDuotone,
    tabIcon: faGearLight,
    permission: null,
    section: 'settings',
    to: '/settings',
  },
]

/** ForServices' menu (services.forhim.io): Sunday, and nothing else. */
const SERVICES_NAV: NavItem[] = [
  {
    label: 'My schedule',
    icon: faCalendarCheckDuotone,
    tabIcon: faCalendarCheckLight,
    permission: null,
    to: '/my-schedule',
  },
  {
    label: 'Plans',
    icon: faListUlDuotone,
    tabIcon: faListUlLight,
    permission: null,
    section: 'plans',
    to: '/services/plans',
    also: ['/services/types'],
  },
  {
    label: 'Songs',
    icon: faMusicDuotone,
    tabIcon: faMusicLight,
    permission: null,
    section: 'songs',
    to: '/services/songs',
    also: ['/services/arrangements'],
  },
  {
    label: 'People',
    icon: faUsersDuotone,
    tabIcon: faUsersLight,
    permission: null,
    section: 'people',
    to: '/services/people',
  },
  {
    label: 'Schedule',
    icon: faCalendarDuotone,
    tabIcon: faCalendarLight,
    permission: null,
    section: 'schedule',
    to: '/services/schedule',
  },
  {
    label: 'Sermons',
    icon: faBookBibleDuotone,
    tabIcon: faBookBibleLight,
    permission: null,
    section: 'sermons',
    to: '/services/sermons',
  },
]

/** The menu this app shows this person. */
function useNav(): NavItem[] {
  const access = useAccess()
  const sections = {
    ...useServicesSections(),
    settings: Object.values(useSettingsSections()).some(Boolean),
  }
  const nav = currentSite() === 'services' ? SERVICES_NAV : CHURCH_NAV
  // ForChurch's entries through the ladders (Viewer of the module); Giving through its role.
  return nav.filter((item) =>
    item.section ? sections[item.section] : !item.permission || access.allows(item.permission),
  )
}

/** In another ForHim app (a full page load there), or in this one. */
const isElsewhere = (item: NavItem) =>
  item.app !== undefined && currentSite() !== null && currentSite() !== item.app

const hrefOf = (item: NavItem) => item.to

/**
 * The menu entry the current address lives under: the longest path that
 * prefixes it, and among the services tabs, the one in `?tab=` (Plans when
 * there is none — a plan's page is under Plans).
 */
function useCurrentEntry(nav: NavItem[]): NavItem | undefined {
  const { pathname } = useLocation()
  const within = (to: string) =>
    to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`)
  // The entry whose address is the longest match (Plans over Services).
  const reach = (item: NavItem) =>
    Math.max(0, ...[item.to, ...(item.also ?? [])].filter(within).map((to) => to.length))
  const matches = nav.filter((item) => !isElsewhere(item) && reach(item) > 0)
  const longest = Math.max(0, ...matches.map(reach))
  return matches.find((m) => reach(m) === longest)
}

/** Follows an in-app address without a page load (modifier clicks still open a tab). */
function useFollow() {
  const navigate = useNavigate()
  return (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return
    event.preventDefault()
    void navigate({ href })
  }
}

/** One menu entry: in the rail (icon, label on hover), the sidebar, or the More sheet. */
function NavEntry({
  item,
  active,
  expanded,
  onNavigate,
}: {
  item: NavItem
  active: boolean
  expanded: boolean
  onNavigate?: () => void
}) {
  const className = `flex items-center rounded-lg text-sm font-medium transition-colors ${
    expanded ? 'gap-3 px-3 py-2' : 'size-10 justify-center'
  } ${
    active
      ? 'bg-brand-soft text-content-brand'
      : 'text-content-muted hover:bg-surface-sunken hover:text-content-default'
  }`
  const label = isElsewhere(item) ? `${item.label} (opens ForServices)` : item.label
  const body = (
    <>
      <Icon icon={item.icon} className="size-5 shrink-0" />
      {expanded && <span className="flex-1 truncate">{item.label}</span>}
      {expanded && isElsewhere(item) && (
        <Icon icon={faArrowUpRightFromSquare} label="Opens ForServices" className="size-3" />
      )}
    </>
  )
  // Another app's page: a plain link there (in development, the same origin).
  if (isElsewhere(item))
    return (
      <a
        href={urlOn('services', hrefOf(item))}
        className={className}
        aria-label={expanded ? undefined : label}
        title={expanded ? undefined : label}
        onClick={onNavigate}
      >
        {body}
      </a>
    )
  return (
    <Link
      to={item.to}
      className={className}
      aria-label={expanded ? undefined : label}
      aria-current={active ? 'page' : undefined}
      title={expanded ? undefined : label}
      onClick={onNavigate}
    >
      {body}
    </Link>
  )
}

/**
 * The tinted top bar: the artwork's colour, deepening to the right. It sets
 * the two ends of the bar's gradient (`.top-bar`), which glide there from the
 * theme's own and back.
 */
const tintGradient = (color: string): CSSProperties =>
  ({
    '--bar-from': color,
    '--bar-to': `color-mix(in oklab, ${color} 70%, black)`,
  }) as CSSProperties

const RAIL_KEY = 'forhim.sidebar'

function readExpanded(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === 'expanded'
  } catch {
    return false
  }
}

function ShellLayout() {
  const { me } = useSession()
  const access = useAccess()
  const services = currentSite() === 'services'
  // The church's plan has no ForServices: say so here rather than show empty pages.
  const locked = services && (me?.grants.lockedModules.includes('services') ?? false)
  const [profileOpen, setProfileOpen] = useState(false)
  const tint = useTopBarTint()
  // A preview stops with the page that played it.
  const pathname = useLocation().pathname
  useEffect(() => stopPreview(), [pathname])

  return (
    <div className="flex min-h-full">
      <Rail />
      <ProfileDrawer open={profileOpen} onOpenChange={setProfileOpen} />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* A wash of the theme's brand and accent; coloured from the page's
            artwork when it has some (a plan's series): a dark island then,
            so its text and icons turn light. */}
        <header
          style={tint ? tintGradient(tint) : undefined}
          className={`top-bar vt-top-bar bar-height shell-x sticky top-0 z-20 flex shrink-0 items-center gap-3 border-b ${
            tint ? 'dark border-transparent text-content-default' : 'border-border-subtle'
          }`}
        >
          <Breadcrumbs />
          <AvatarButton onClick={() => setProfileOpen(true)} />
          <RouteProgress />
        </header>

        <ConnectionBanner />
        <StaleBanner />
        <AccountBanner />
        {me && !me.user.emailVerified && <VerifyBanner email={me.user.email} />}

        {/* Not a scroll box (no `overflow-auto`): the window scrolls, so a
            list's sticky title bar can stick under the top bar. */}
        <main className="shell-x clear-tabs min-w-0 flex-1 overflow-x-clip pt-6 sm:pb-6 lg:py-8">
          {locked ? (
            <Card className="mx-auto max-w-lg space-y-2 p-6">
              <h1 className="text-lg font-semibold text-content-default">
                ForServices is not in your church&rsquo;s plan
              </h1>
              <p className="text-sm text-content-muted">
                {access.has('org', 'administrator', { orgWide: true })
                  ? 'Choose a plan that includes it in ForChurch → Settings → Billing.'
                  : 'Ask your church’s administrator about adding it.'}
              </p>
            </Card>
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      <BottomTabs />
      {/* The one audio element every song preview plays through. */}
      <PreviewAudio />
    </div>
  )
}

/**
 * The menu from `sm` up: icons only by default, with a toggle that widens it
 * to the labelled sidebar. It stays put while the page scrolls.
 */
function Rail() {
  const nav = useNav()
  const current = useCurrentEntry(nav)
  const services = currentSite() === 'services'
  const [expanded, setExpanded] = useState(readExpanded)
  const toggle = () => {
    setExpanded(!expanded)
    try {
      localStorage.setItem(RAIL_KEY, expanded ? 'collapsed' : 'expanded')
    } catch {
      // Remembered for this visit only.
    }
  }

  return (
    <aside
      className={`vt-rail rail-inset sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-border-subtle bg-surface-raised sm:flex ${
        expanded ? 'w-60' : 'w-16'
      }`}
    >
      <div
        className={`bar-height flex shrink-0 items-center gap-2 border-b border-border-subtle ${
          expanded ? 'px-4' : 'justify-center'
        }`}
      >
        <Icon
          icon={services ? faMusicDuotone : faChurchDuotone}
          label={services ? 'ForServices' : 'ForChurch'}
          className="size-6 shrink-0 text-content-brand"
        />
        {expanded && (
          <span className="truncate text-sm font-semibold text-content-default">
            {services ? 'ForServices' : 'ForChurch'}
          </span>
        )}
      </div>

      <nav
        aria-label="Main"
        className={`no-scrollbar flex flex-1 flex-col gap-1 overflow-y-auto py-3 ${
          expanded ? 'px-3' : 'items-center'
        }`}
      >
        {nav.map((item) => (
          <NavEntry key={item.label} item={item} active={item === current} expanded={expanded} />
        ))}
        {services && (
          <a
            href={urlOn('church', '/')}
            className={`mt-4 flex items-center rounded-lg text-sm font-medium text-content-muted hover:bg-surface-sunken hover:text-content-default ${
              expanded ? 'gap-3 px-3 py-2' : 'size-10 justify-center'
            }`}
            aria-label={expanded ? undefined : 'ForChurch (opens ForChurch)'}
            title={expanded ? undefined : 'ForChurch'}
          >
            <Icon icon={faChurchDuotone} className="size-5 shrink-0" />
            {expanded && <span className="flex-1">ForChurch</span>}
            {expanded && (
              <Icon icon={faArrowUpRightFromSquare} label="Opens ForChurch" className="size-3" />
            )}
          </a>
        )}
      </nav>

      <div
        className={`flex shrink-0 border-t border-border-subtle p-3 ${
          expanded ? 'justify-end' : 'justify-center'
        }`}
      >
        <IconButton
          icon={faSidebar}
          label={expanded ? 'Collapse menu' : 'Expand menu'}
          aria-expanded={expanded}
          className="text-content-muted"
          onClick={toggle}
        />
      </div>
    </aside>
  )
}

/**
 * A tab, tapped: it gives under the finger and springs back. The Web
 * Animations API rather than a class, so the tab's re-render as it becomes
 * the current one doesn't cut it short.
 */
function pressTab(event: MouseEvent<HTMLElement>) {
  const tab = (event.target as Element).closest('a, button')
  if (!tab || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  tab.animate(
    [
      { transform: 'scale(1)' },
      { transform: 'scale(0.86)', offset: 0.35 },
      { transform: 'scale(1)' },
    ],
    { duration: 260, easing: 'ease-out' },
  )
}

/**
 * A phone's menu: tabs along the bottom, each an icon with its name under it.
 * Up to five fit; with more, the four that suit this person (choosePhoneTabs:
 * the directory for staff, the check-in desk for its volunteers, My schedule
 * for those who serve) and More, which lists the rest in a sheet.
 */
function BottomTabs() {
  const nav = useNav()
  const current = useCurrentEntry(nav)
  const [moreOpen, setMoreOpen] = useState(false)
  const access = useAccess()
  const tabs = choosePhoneTabs(nav, (permission) => access.allows(permission as Permission))
  const rest = nav.filter((item) => !tabs.includes(item))
  const services = currentSite() === 'services'
  const moreActive = current !== undefined && rest.includes(current)
  // A name under each icon: tight tracking so five fit a 360px phone whole.
  // Low in the bar (justify-end), just off its bottom edge (tab-bar-inset).
  const tabClass = (active: boolean) =>
    `relative flex h-14 min-w-0 flex-1 flex-col items-center justify-end gap-1 px-0.5 pb-1 transition-colors duration-(--duration-fast) ease-out ${
      active ? 'text-content-brand' : 'text-content-muted'
    }`
  const tabLabel = (label: string) => (
    <span className="max-w-full truncate text-xs leading-none font-medium tracking-tight">
      {label}
    </span>
  )
  // The bar over the chosen tab grows into place as the tab is chosen.
  const indicator = (active: boolean) => (
    <span
      aria-hidden
      className={`absolute inset-x-5 top-0 h-0.5 rounded-full bg-brand-solid transition-transform duration-(--duration-base) ease-out ${
        active ? 'scale-x-100' : 'scale-x-0'
      }`}
    />
  )

  return (
    <>
      <nav
        aria-label="Main"
        className="vt-tab-bar tab-bar-inset fixed inset-x-0 bottom-0 z-20 flex border-t border-border-subtle bg-surface-tab-bar sm:hidden"
        onClick={pressTab}
      >
        {tabs.map((item) =>
          isElsewhere(item) ? (
            <a
              key={item.label}
              href={urlOn('services', hrefOf(item))}
              aria-label={`${item.label} (opens ForServices)`}
              className={tabClass(false)}
            >
              {indicator(false)}
              <Icon icon={item.tabIcon} className="size-6" />
              {tabLabel(item.label)}
            </a>
          ) : (
            <Link
              key={item.label}
              to={item.to}
              aria-current={item === current ? 'page' : undefined}
              className={tabClass(item === current)}
            >
              {indicator(item === current)}
              <Icon icon={item.tabIcon} className="size-6" />
              {tabLabel(item.label)}
            </Link>
          ),
        )}
        {rest.length > 0 && (
          <button
            type="button"
            aria-haspopup="dialog"
            className={tabClass(moreActive)}
            onClick={() => setMoreOpen(true)}
          >
            {indicator(moreActive)}
            <Icon icon={faEllipsisLight} className="size-6" />
            {tabLabel('More')}
          </button>
        )}
      </nav>

      <Drawer.Root open={moreOpen} onOpenChange={setMoreOpen} swipeDirection="down">
        <Drawer.Portal>
          <Drawer.Backdrop className="drawer-backdrop fixed inset-0 bg-black/40" />
          <Drawer.Viewport className="fixed inset-0 flex items-end pt-16">
            <Drawer.Popup className="detail-sheet safe-bottom max-h-full w-full overflow-y-auto overscroll-contain rounded-t-sheet border-t border-border-subtle bg-surface-overlay shadow-2xl outline-none">
              <div className="border-b border-border-default">
                <SheetHandle side="top" />
                <Drawer.Title className="px-5 pt-1 pb-3 text-base font-semibold text-content-default">
                  More
                </Drawer.Title>
              </div>
              <div className="grid grid-cols-3 gap-1 p-3">
                {rest.map((item) => {
                  const active = item === current
                  const className = `flex flex-col items-center gap-2 rounded-xl px-2 py-4 text-center text-sm font-medium ${
                    active
                      ? 'bg-brand-soft text-content-brand'
                      : 'text-content-default hover:bg-surface-sunken'
                  }`
                  return isElsewhere(item) ? (
                    <a
                      key={item.label}
                      href={urlOn('services', hrefOf(item))}
                      className={className}
                    >
                      <Icon icon={item.icon} className="size-6 text-content-brand" />
                      <span className="flex items-center gap-1">
                        {item.label}
                        <Icon
                          icon={faArrowUpRightFromSquare}
                          label="Opens ForServices"
                          className="size-3 text-content-muted"
                        />
                      </span>
                    </a>
                  ) : (
                    <Link
                      key={item.label}
                      to={item.to}
                      aria-current={active ? 'page' : undefined}
                      className={className}
                      onClick={() => setMoreOpen(false)}
                    >
                      <Icon icon={item.icon} className="size-6 text-content-brand" />
                      {item.label}
                    </Link>
                  )
                })}
                {services && (
                  <a
                    href={urlOn('church', '/')}
                    className="flex flex-col items-center gap-2 rounded-xl px-2 py-4 text-sm font-medium text-content-default hover:bg-surface-sunken"
                  >
                    <Icon icon={faChurchDuotone} className="size-6 text-content-brand" />
                    ForChurch
                  </a>
                )}
              </div>
            </Drawer.Popup>
          </Drawer.Viewport>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  )
}

/**
 * Where you are: the menu entry, then whatever trail the page added
 * (`useBreadcrumbs`), the last one being the page itself. On a phone, just
 * the way back (the parent) and the page.
 */
function Breadcrumbs() {
  const nav = useNav()
  const entry = useCurrentEntry(nav)
  const pageCrumbs = usePageCrumbs()
  const follow = useFollow()
  const root: Crumb | null = entry ? { label: entry.label, href: hrefOf(entry) } : null
  // ForServices' Plans entry is already "Plans": a page crumb saying it again is dropped.
  const crumbs = [
    ...(root ? [root] : []),
    ...pageCrumbs.filter((c, i) => !(i === 0 && c.label === root?.label)),
  ]
  const last = crumbs.length - 1
  const parent = [...crumbs.slice(0, last)].reverse().find((c) => c.href)
  // The tab names the page and its app: "Plans · ForServices".
  const { pathname } = useLocation()
  useDocumentTitle(documentTitle(crumbs[last]?.label, appName(pathname)))

  const link = (crumb: Crumb, className: string, children: ReactNode, label?: string) =>
    crumb.href ? (
      <a
        href={crumb.href}
        className={className}
        aria-label={label}
        onClick={(event) => crumb.href && follow(event, crumb.href)}
      >
        {children}
      </a>
    ) : (
      <span className={className}>{children}</span>
    )

  return (
    <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
      {/* A phone: back to the parent, and where you are. */}
      <div className="flex min-w-0 items-center gap-1 sm:hidden">
        {last > 0 && parent?.href
          ? link(
              parent,
              '-ml-2 inline-flex size-10 shrink-0 no-underline items-center justify-center rounded-lg text-content-muted hover:bg-surface-sunken',
              <Icon icon={faChevronLeft} />,
              `Back to ${parent.label}`,
            )
          : entry && <Icon icon={entry.icon} className="size-5 shrink-0 text-content-brand" />}
        <span aria-current="page" className="truncate text-base font-bold text-content-default">
          {crumbs[last]?.label ?? ''}
        </span>
      </div>
      {/* Wider: the whole trail. */}
      <ol className="hidden min-w-0 items-center gap-1.5 text-sm sm:flex">
        {crumbs.map((crumb, i) => (
          <li
            key={`${i}-${crumb.label}`}
            className={`flex items-center gap-1.5 ${i === last ? 'min-w-0' : 'shrink-0'}`}
          >
            {i > 0 && (
              <Icon icon={faChevronRight} className="size-3 shrink-0 text-content-subtle" />
            )}
            {i === last ? (
              <span
                aria-current="page"
                className="flex min-w-0 items-center gap-2 font-semibold text-content-default"
              >
                {i === 0 && entry && (
                  <Icon icon={entry.icon} className="size-4 shrink-0 text-content-brand" />
                )}
                <span className="truncate">{crumb.label}</span>
              </span>
            ) : (
              link(
                crumb,
                'flex items-center gap-2 rounded text-content-muted no-underline hover:text-content-default',
                <>
                  {i === 0 && entry && (
                    <Icon icon={entry.icon} className="size-4 shrink-0 text-content-brand" />
                  )}
                  {crumb.label}
                </>,
              )
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}

const initialsOf = (name: string | undefined) =>
  (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?'

function Avatar({ size }: { size: 'sm' | 'lg' }) {
  const { me } = useSession()
  const box = size === 'sm' ? 'size-9 text-sm' : 'size-14 text-lg'
  if (me?.user.image)
    return (
      <img src={me.user.image} alt="" className={`${box} shrink-0 rounded-full object-cover`} />
    )
  return (
    <span
      aria-hidden
      className={`${box} inline-flex shrink-0 items-center justify-center rounded-full bg-brand-solid font-semibold text-brand-on-solid`}
    >
      {initialsOf(me?.user.name)}
    </span>
  )
}

function AvatarButton({ onClick }: { onClick: () => void }) {
  const { me } = useSession()
  return (
    <button
      type="button"
      aria-label={`Your account${me ? ` (${me.user.name})` : ''}`}
      title="Your account"
      aria-haspopup="dialog"
      onClick={onClick}
      className="-mr-1 shrink-0 rounded-full p-0.5 hover:ring-2 hover:ring-border-default focus-visible:ring-2 focus-visible:ring-border-brand focus-visible:outline-none"
    >
      <Avatar size="sm" />
    </button>
  )
}

const THEMES: { value: ThemeChoice; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'graphite', label: 'Graphite' },
  { value: 'ember', label: 'Ember' },
  { value: 'twilight', label: 'Twilight' },
  { value: 'olive', label: 'Olive' },
  { value: 'linen', label: 'Linen' },
]

/** A theme's look in miniature, in its own colours (its tokens, scoped to the swatch). */
function ThemeSwatch({ theme }: { theme: Exclude<ThemeChoice, 'system'> }) {
  const scope =
    theme === 'light' ? { className: 'light' } : theme === 'dark' ? { className: 'dark' } : {}
  return (
    <span
      {...(theme === 'light' || theme === 'dark' ? {} : { 'data-theme': theme })}
      className={`flex h-full w-full flex-col justify-between bg-surface-base p-1.5 ${scope.className ?? ''}`}
    >
      <span className="flex flex-col gap-1">
        <span className="h-1 w-3/4 rounded-full bg-content-default" />
        <span className="h-1 w-1/2 rounded-full bg-content-muted" />
      </span>
      <span className="flex items-center gap-1">
        <span className="h-2 w-5 rounded-full bg-brand-solid" />
        <span className="size-2 rounded-full bg-accent-solid" />
      </span>
    </span>
  )
}

/**
 * Your account, from the avatar: who you are, which church, the
 * theme, the other ForHim app, and signing out. Slides in from the right, under the avatar.
 */
function ProfileDrawer({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { me } = useSession()
  const settingsShown = Object.values(useSettingsSections()).some(Boolean)
  const inServices = useServicesSections().any
  const signOut = useSignOut()
  const navigate = useNavigate()
  const theme = useTheme()
  const phone = useMediaQuery(PHONE_QUERY)
  const services = currentSite() === 'services'
  const close = () => onOpenChange(false)
  const rowClass =
    'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-content-default hover:bg-surface-sunken'

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} swipeDirection={phone ? 'down' : 'right'}>
      <Drawer.Portal>
        <Drawer.Backdrop className="drawer-backdrop fixed inset-0 z-40 bg-black/40" />
        {/* The same shape as every drawer: a sheet on a phone, a panel from sm up. */}
        <Drawer.Viewport className={phone ? sheetViewport : panelViewport}>
          <Drawer.Popup
            className={`${phone ? sheetPopup : `${panelPopup} sm:max-w-80`} safe-bottom overflow-y-auto overscroll-contain`}
          >
            <SheetHandle side={phone ? 'top' : 'left'} />
            <div className="flex items-start gap-3 border-b border-border-default p-4">
              <Avatar size="lg" />
              <div className="min-w-0 flex-1 pt-1">
                <Drawer.Title className="truncate text-base font-semibold text-content-default">
                  {me?.user.name}
                </Drawer.Title>
                <p className="truncate text-sm text-content-muted">{me?.user.email}</p>
              </div>
              <Drawer.Close
                aria-label="Close"
                title="Close"
                className="-mt-1 -mr-2 inline-flex size-10 shrink-0 items-center justify-center rounded-lg text-content-muted hover:bg-surface-sunken"
              >
                <Icon icon={faXmark} />
              </Drawer.Close>
            </div>

            <DrawerSection title="Church">
              <ChurchList onSwitched={close} />
            </DrawerSection>

            <DrawerSection title="Theme">
              <div role="radiogroup" aria-label="Theme" className="grid grid-cols-4 gap-2">
                {THEMES.map((t) => {
                  const chosen = theme.choice === t.value
                  return (
                    <button
                      key={t.value}
                      type="button"
                      role="radio"
                      aria-checked={chosen}
                      onClick={() => theme.choose(t.value)}
                      className="group flex flex-col items-center gap-1.5 rounded-lg p-1 text-xs font-medium text-content-muted transition-colors hover:text-content-default aria-checked:text-content-default"
                    >
                      <span
                        className={`relative flex h-11 w-full overflow-hidden rounded-md border transition-[box-shadow,border-color] duration-200 ${
                          chosen
                            ? 'border-border-brand ring-2 ring-border-brand'
                            : 'border-border-default group-hover:border-border-strong'
                        }`}
                      >
                        {t.value === 'system' ? (
                          <>
                            <span className="w-1/2 overflow-hidden">
                              <ThemeSwatch theme="light" />
                            </span>
                            <span className="w-1/2 overflow-hidden">
                              <ThemeSwatch theme="dark" />
                            </span>
                          </>
                        ) : (
                          <ThemeSwatch theme={t.value} />
                        )}
                      </span>
                      {t.label}
                    </button>
                  )
                })}
              </div>
              {designToolsEnabled && (
                <DesignEditorRow
                  className={rowClass}
                  onOpen={() => {
                    close()
                    openDesignEditor()
                  }}
                />
              )}
            </DrawerSection>

            <DrawerSection title="Account">
              <a href={urlOn('id', '/apps')} className={rowClass}>
                <Icon icon={faIdCard} className="size-4 text-content-muted" />
                <span className="flex-1">Your ForHim account</span>
              </a>
              {settingsShown &&
                (services ? (
                  <a href={urlOn('church', '/settings')} className={rowClass}>
                    <Icon icon={faGear} className="size-4 text-content-muted" />
                    <span className="flex-1">Church settings</span>
                    <Icon
                      icon={faArrowUpRightFromSquare}
                      label="Opens ForChurch"
                      className="size-3 text-content-muted"
                    />
                  </a>
                ) : (
                  <Link to="/settings" className={rowClass} onClick={close}>
                    <Icon icon={faGear} className="size-4 text-content-muted" />
                    <span className="flex-1">Church settings</span>
                  </Link>
                ))}
              {services ? (
                <a href={urlOn('church', '/')} className={rowClass}>
                  <Icon icon={faChurchDuotone} className="size-4 text-content-brand" />
                  <span className="flex-1">Open ForChurch</span>
                  <Icon
                    icon={faArrowUpRightFromSquare}
                    label="Opens ForChurch"
                    className="size-3 text-content-muted"
                  />
                </a>
              ) : (
                inServices && (
                  <a href={urlOn('services', '/services')} className={rowClass}>
                    <Icon icon={faMusicDuotone} className="size-4 text-content-brand" />
                    <span className="flex-1">Open ForServices</span>
                    {currentSite() !== null && (
                      <Icon
                        icon={faArrowUpRightFromSquare}
                        label="Opens ForServices"
                        className="size-3 text-content-muted"
                      />
                    )}
                  </a>
                )
              )}
            </DrawerSection>

            <div className="mt-auto border-t border-border-subtle p-3">
              <button
                type="button"
                className={rowClass}
                disabled={signOut.isPending}
                onClick={() =>
                  signOut.mutate(undefined, {
                    onSuccess: () =>
                      void navigate({ to: '/sign-in', search: { redirect: undefined } }),
                  })
                }
              >
                {signOut.isPending ? (
                  <InlineSpinner className="size-4 text-content-muted" />
                ) : (
                  <Icon icon={faRightFromBracket} className="size-4 text-content-muted" />
                )}
                {signOut.isPending ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

/** Staging and development only: the design editor, with its unsaved changes counted. */
function DesignEditorRow({ className, onOpen }: { className: string; onOpen: () => void }) {
  const { count } = useDesignTweaks()
  return (
    <button type="button" className={className} onClick={onOpen}>
      <Icon icon={faPalette} className="size-4 text-content-muted" />
      <span className="flex-1">Design editor</span>
      {count > 0 && (
        <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-brand-solid px-1.5 text-xs font-semibold text-brand-on-solid tabular-nums">
          {count}
        </span>
      )}
    </button>
  )
}

function DrawerSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2 border-b border-border-subtle p-4 last-of-type:border-0">
      <h3 className="px-3 text-xs font-semibold tracking-wide text-content-subtle uppercase">
        {title}
      </h3>
      {children}
    </section>
  )
}

/** The churches this login belongs to: the current one ticked, the others a tap away. */
function ChurchList({ onSwitched }: { onSwitched: () => void }) {
  const { org, memberships } = useOrg()
  const switchOrg = useSwitchOrg()
  const router = useRouter()
  const navigate = useNavigate()
  const [switching, setSwitching] = useState<string | null>(null)
  const [failed, setFailed] = useState<{ id: string; name: string } | null>(null)
  const go = (id: string, name: string) => {
    setSwitching(id)
    setFailed(null)
    switchOrg(id)
      .then(async () => {
        await router.invalidate()
        onSwitched()
        notify({ title: `Now in ${name}`, description: 'Everything you see is this church’s.' })
      })
      .catch(() => setFailed({ id, name }))
      .finally(() => setSwitching(null))
  }
  return (
    <ul className="space-y-0.5">
      {failed && (
        <li
          role="alert"
          className="flex items-center justify-between gap-2 rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-content"
        >
          <span>Couldn’t switch to {failed.name}. You’re still in this church.</span>
          <button
            type="button"
            className="shrink-0 font-semibold underline-offset-2 hover:underline"
            onClick={() => go(failed.id, failed.name)}
          >
            Try again
          </button>
        </li>
      )}
      {memberships.map((m) => {
        const current = m.organizationId === org?.organizationId
        return (
          <li key={m.organizationId}>
            <button
              type="button"
              disabled={current || switching !== null}
              aria-current={current ? 'true' : undefined}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm ${
                current
                  ? 'font-semibold text-content-default'
                  : 'text-content-default hover:bg-surface-sunken'
              }`}
              onClick={() => go(m.organizationId, m.organizationName)}
            >
              <Icon
                icon={faChurchDuotone}
                className={`size-4 shrink-0 ${current ? 'text-content-brand' : 'text-content-muted'}`}
              />
              <span className="flex-1 truncate">
                {switching === m.organizationId
                  ? `Switching to ${m.organizationName}…`
                  : m.organizationName}
              </span>
              {switching === m.organizationId && (
                <InlineSpinner className="size-3.5 text-content-brand" />
              )}
              {current && (
                <Icon icon={faCheck} label="Current" className="size-3.5 text-content-brand" />
              )}
            </button>
          </li>
        )
      })}
      <li>
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-content-brand hover:bg-surface-sunken"
          onClick={() => {
            onSwitched()
            void navigate({ to: '/onboarding' })
          }}
        >
          <Icon icon={faPlus} className="size-4" />
          New church
        </button>
      </li>
    </ul>
  )
}

/**
 * What's on screen is older than it looks: a refresh failed, so a page is
 * showing what it last had. Says since when, and offers Retry. (Offline has
 * its own banner, which says more.)
 */
function StaleBanner() {
  const stale = useStaleData()
  if (stale.count === 0 || !navigator.onLine) return null
  const at = stale.since?.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 border-b border-border-subtle bg-warning-soft px-4 py-1.5 text-sm text-warning-content sm:px-6"
    >
      <span>
        {at ? `Showing what was saved at ${at}` : 'Showing what was saved earlier'} — couldn’t
        refresh{stale.failure?.kind === 'server' ? ' (the server had a problem)' : ''}.
      </span>
      <Button
        variant="ghost"
        className="-my-1 shrink-0 text-warning-content"
        pending={stale.retrying}
        pendingLabel="Refreshing…"
        onClick={stale.retry}
      >
        Retry
      </Button>
    </div>
  )
}

/** Unverified email: where the link went, and a way to send it again. */
function VerifyBanner({ email }: { email: string }) {
  const resend = useResendVerification()
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border-subtle bg-warning-soft px-4 py-2 text-sm text-warning-content sm:px-6">
      <span>
        {resend.isSuccess
          ? 'Sent again — check '
          : 'Verify your email address — we sent a link to '}
        <span className="font-medium break-all">{email}</span>
        {resend.isSuccess ? ' (and its spam folder).' : '.'}
      </span>
      {!resend.isSuccess && (
        <Button
          variant="ghost"
          className="-my-1 shrink-0 text-warning-content"
          pending={resend.isPending}
          pendingLabel="Sending…"
          onClick={() =>
            resend.mutate(
              { email, callbackURL: window.location.href },
              {
                onError: () =>
                  notify({
                    tone: 'error',
                    title: 'Couldn’t send the email',
                    description: 'Nothing changed. Try again in a minute.',
                  }),
              },
            )
          }
        >
          Resend email
        </Button>
      )}
    </div>
  )
}

/** Impersonation, read-only accounts: the one notice that outranks everything else. */
function AccountBanner() {
  const notice = useAccountNotice()
  const stop = useStopImpersonating()
  if (!notice) return null
  const tone =
    notice.tone === 'danger'
      ? 'bg-danger-solid text-danger-on-solid'
      : notice.tone === 'warning'
        ? 'bg-warning-soft text-warning-content'
        : 'bg-brand-soft text-content-brand'
  return (
    <div
      role="alert"
      className={`flex flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm sm:px-6 ${tone}`}
    >
      <span>{notice.message}</span>
      {notice.action &&
        ('to' in notice.action ? (
          <Link to={notice.action.to} className="font-semibold underline">
            {notice.action.label}
          </Link>
        ) : (
          <button
            type="button"
            className="rounded-md bg-surface-raised px-3 py-1 font-semibold text-danger-solid"
            onClick={() =>
              stop.mutate(undefined, { onSuccess: () => window.location.assign('/admin') })
            }
          >
            {notice.action.label}
          </button>
        ))}
    </div>
  )
}
